/**
 * WCAG 2.1 contrast helpers for tenant brand and sidebar foreground derivation.
 */

export type Rgb = readonly [r: number, g: number, b: number];

const NEAR_WHITE: Rgb = [255, 255, 255];
const NEAR_BLACK: Rgb = [15, 23, 42];

/** Primary scale endpoints used on brand fills (matches `--color-blue-*`). */
const PRIMARY_ON_DARK: Rgb = [147, 197, 253]; // blue-300
const PRIMARY_ON_DARK_HOVERED: Rgb = [96, 165, 250]; // blue-400
const PRIMARY_ON_LIGHT: Rgb = [29, 78, 216]; // blue-700
const PRIMARY_ON_LIGHT_HOVERED: Rgb = [30, 64, 175]; // blue-800

function srgbChannelToLinear(channel: number): number {
  const s = channel / 255;

  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

/** Relative luminance (WCAG 2.1). */
export function relativeLuminance(rgb: Rgb): number {
  const [r, g, b] = rgb.map(srgbChannelToLinear) as [number, number, number];

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Contrast ratio between two colors (WCAG 2.1, larger first). */
export function contrastRatio(foreground: Rgb, background: Rgb): number {
  const L1 = relativeLuminance(foreground);
  const L2 = relativeLuminance(background);
  const lighter = Math.max(L1, L2);
  const darker = Math.min(L1, L2);

  return (lighter + 0.05) / (darker + 0.05);
}

/** True when the pair meets WCAG 2.1 AA for normal text (≥ 4.5:1). */
export function meetsAaNormalText(
  foreground: Rgb,
  background: Rgb,
): boolean {
  return contrastRatio(foreground, background) >= 4.5;
}

const HEX_6_PATTERN = /^#?([0-9a-fA-F]{6})$/;

/** Parses a six-digit hex color into sRGB channels. */
export function parseHexColor(hex: string | undefined): Rgb | undefined {
  const normalized = hex?.trim();
  if (!normalized) {
    return undefined;
  }

  const match = normalized.match(HEX_6_PATTERN);
  if (!match) {
    return undefined;
  }

  const value = match[1];
  const r = Number.parseInt(value.slice(0, 2), 16);
  const g = Number.parseInt(value.slice(2, 4), 16);
  const b = Number.parseInt(value.slice(4, 6), 16);

  return [r, g, b];
}

/** Formats sRGB channels as a six-digit hex color. */
export function formatHexColor([r, g, b]: Rgb): string {
  const toHex = (channel: number) => channel.toString(16).padStart(2, "0");

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

type ForegroundCandidate = {
  ratio: number;
  foreground: Rgb;
};

function pickBestForeground(
  background: Rgb,
  candidates: readonly Rgb[],
  fallback: Rgb,
): Rgb {
  const scored: ForegroundCandidate[] = candidates.map((foreground) => ({
    foreground,
    ratio: contrastRatio(foreground, background),
  }));

  const aaCandidate = scored.find((candidate) =>
    meetsAaNormalText(candidate.foreground, background),
  );
  if (aaCandidate) {
    return aaCandidate.foreground;
  }

  return scored.reduce(
    (best, candidate) => (candidate.ratio > best.ratio ? candidate : best),
    { ratio: -1, foreground: fallback },
  ).foreground;
}

/**
 * Picks a readable foreground hex for text/icons on a brand or sidebar swatch.
 * Prefers AA-normal-text compliance; otherwise returns the higher-contrast candidate.
 */
export function pickReadableForegroundOn(backgroundHex: string | undefined): string {
  const background = parseHexColor(backgroundHex);
  if (!background) {
    return formatHexColor(NEAR_WHITE);
  }

  return formatHexColor(
    pickBestForeground(background, [NEAR_WHITE, NEAR_BLACK], NEAR_WHITE),
  );
}

export type PrimaryOnBrandPair = {
  base: string;
  hovered: string;
};

/**
 * Picks a primary-tint pair for accents (e.g. PII badges) on a brand swatch.
 * Uses the same light/dark polarity as {@link pickReadableForegroundOn}, but
 * stays on the primary scale so accents remain distinct from body text.
 */
export function pickReadablePrimaryOn(
  backgroundHex: string | undefined,
): PrimaryOnBrandPair {
  const background = parseHexColor(backgroundHex);
  if (!background) {
    return {
      base: formatHexColor(PRIMARY_ON_DARK),
      hovered: formatHexColor(PRIMARY_ON_DARK_HOVERED),
    };
  }

  const polarity = pickBestForeground(
    background,
    [NEAR_WHITE, NEAR_BLACK],
    NEAR_WHITE,
  );
  const useLightPrimary = formatHexColor(polarity) === formatHexColor(NEAR_WHITE);

  return {
    base: formatHexColor(useLightPrimary ? PRIMARY_ON_DARK : PRIMARY_ON_LIGHT),
    hovered: formatHexColor(
      useLightPrimary ? PRIMARY_ON_DARK_HOVERED : PRIMARY_ON_LIGHT_HOVERED,
    ),
  };
}
